import React, { useEffect, useMemo, useState, useRef } from 'react';
import DepartmentTabsContent from '@/features/department/components/DepartmentTabsContent';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { get, post } from '@/features/shared/lib/api';
import { arr } from '@/features/shared/lib/utils';
import AuthorsTable from '@/features/publication/components/AuthorsTable';
import ReferencesTable from '@/features/publication/components/ReferencesTable';
import AuthorOtherPapers from '@/features/publication/components/AuthorOtherPapers';
import AuthorsByCountryDonut from '@/features/publication/components/charts/AuthorsByCountryDonut';
import AuthorsByInstitutionDonut from '@/features/publication/components/charts/AuthorsByInstitutionDonut';
import TopCategoriesByDB from "@/features/journal/components/charts/TopCategoriesByDB";
import DepartmentCollaborationBar from "@/features/department/chart/DepartmentCollaborationBar";
import DepartmentOutput5YearChart from "@/features/department/chart/DepartmentOutput5YearChart";
import KeywordCloud from "@/features/researcher/components/charts/KeywordCloud";
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';
import DepartmentMostCitedResearcherCard from '@/features/department/components/DepartmentMostCitedResearcherCard';
import DepartmentTopPublishingResearcherCard from '@/features/department/components/DepartmentTopPublishingResearcherCard';

export default function Department() {
  const { id: pCode } = useParams();
  const [sp] = useSearchParams();
  // 기본: /departments/:code, 예비: /departments?code=xxxxx
  const code = pCode || sp.get('category_code') || sp.get('category_code');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [keywordData, setKeywordData] = useState([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  const navigate = useNavigate();
  const scholarsListRef = useRef(null);

  // 연도 기준: 2월까지는 전년도, 3월부터는 해당년도
  const reportYear = useMemo(() => {
    try {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth() + 1; // 1-12
      return m <= 2 ? y - 1 : y;
    } catch {
      return new Date().getFullYear();
    }
  }, []);

  // topresearcher_stats_year: 1 → 해당년도만, 2 → 전년~해당년, 3 → 2년전~해당년
  const { startYear: topStartYear, endYear: topEndYear } = useMemo(() => {
    const statsYear = Math.max(1, Math.min(5, Number(record?.topresearcher_stats_year) || 1));
    const endYear = reportYear;
    const startYear = endYear - (statsYear - 1);
    return { startYear, endYear };
  }, [record?.topresearcher_stats_year, reportYear]);
  const onBackClick = (e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch {
    }
    // Prefer browser back to leverage list page POP hydration
    try {
      navigate(-1);
    } catch {
      // Fallback to list route if history is empty
      navigate('/departments');
    }
  };

  const handlePrint = (e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch {
    }
    try {
      if (typeof window !== 'undefined' && typeof window.print === 'function') {
        window.print();
      }
    } catch (err) {
      console.error('Print error:', err);
    }
  };
  useEffect(() => {
    if (!code) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await get(`/rm/v1/department/${encodeURIComponent(code)}`);
        if (!alive) return;
        const rec = data?.result?.record || data?.result || null;
        setRecord(rec);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [code]);

  useEffect(() => {
    if (!record) return;
    const en = String(record.category_name_en ?? '').trim();
    const ko = String(record.category_name ?? '').trim();
    const up = String(record.up_category_name ?? '').trim();
    const label = en || ko || up;
    if (!label) return;
    const site =
      typeof rmVars !== 'undefined' && rmVars.siteName
        ? String(rmVars.siteName).trim()
        : '';
    document.title = site ? `${label} - Department - ${site}` : label;
  }, [record]);

  // 키워드 클라우드 API 호출
  useEffect(() => {
    if (!record?.id && !record?.category_id) return;

    let alive = true;
    const fetchKeywordCloud = async () => {
      setKeywordLoading(true);
      try {
        const deptId = record?.id || record?.category_id;
        const requestBody = {
          cloudType: 'dept',
          query: {
            dept_id: deptId,
            startYear: 1999,
            endYear: 3000
          }
        };

        const response = await post('/rm/v1/stats/keyword_cloud', requestBody);

        if (!alive) return;

        // API 응답에서 records 추출
        let rawRecords = [];
        if (response?.result?.records && Array.isArray(response.result.records)) {
          rawRecords = response.result.records;
        } else if (response?.records && Array.isArray(response.records)) {
          rawRecords = response.records;
        } else if (Array.isArray(response)) {
          rawRecords = response;
        }

        const keywords = rawRecords
          .map((item) => ({
            keyword: item.text || item.keyword || item.label || '',
            count: item.size || item.count || item.value || 1,
          }))
          .filter((item) => item.keyword)

        setKeywordData(keywords);
      } catch (error) {
        if (!alive) return;
        console.error('Error fetching keyword cloud:', error);
        setKeywordData([]);
      } finally {
        if (alive) {
          setKeywordLoading(false);
        }
      }
    };

    fetchKeywordCloud();
    return () => { alive = false; };
  }, [record?.id, record?.category_id]);

  const allScholars = record?.scholar && Array.isArray(record.scholar) ? record.scholar : [];

  if (!code) {
    return (
      <div className="p-6 mt-[127px]">잘못된 접근입니다. 부서 코드가 없습니다.</div>
    );
  }
  if (err) {
    return (
      <div className="p-6 mt-[127px] text-red-600">에러: {String(err)}</div>
    );
  }

  return (
    <div className="detailPage flexPage !gap-[40px] mt-[127px] lg:mt-[120px] lg:px-6 xl:px-[100px]">
      <div className="w-full px-6 pt-[24px] lg:relative lg:w-[calc((100%-40px)*0.7)] lg:mt-[105px] lg:bg-white lg:rounded-xl lg:px-[60px] lg:py-[60px] lg:pt-[60px]">

        <button
          type="button"
          aria-label="Back to Department list"
          onClick={onBackClick}
          className="inline-flex items-center gap-[6px] bg-[#F3F5F9] mb-6 py-[10px] pl-[10px] pr-[16px] rounded-md lg:pl-0 lg:pr-0 lg:py-0 lg:bg-transparent lg:absolute lg:-top-[44px] lg:left-0"
        >
          <img src={rmVars.themeUrl + 'images/chevron_right_8D9AAF.png'} className="w-3 rotate-180" alt="back" />
          <span className="text-[14px] text-[#6C7687] font-medium leading-[100%] lg:text-[16px] xxl:text-red-500">Back<span className="hidden lg:inline"> to Department list</span></span>
        </button>

        <button
          type="button"
          className="btn-print hidden px-3 py-[6px] items-center gap-[6px] bg-white rounded-[8px] cursor-pointer absolute lg:-top-[54px] lg:right-0 lg:flex"
          onClick={handlePrint}
        >
          <img src={rmVars.themeUrl + 'images/print-ico.png'} className="w-[24px] h-[24px]" alt="print" />
          <span className="text-[14px] text-[#6C7687] font-semibold leading-[100%]">Print</span>
        </button>

        <div>
          <div className="xl:flex items-center gap-8">
            <div className="w-full h-[134px] mb-5 rounded-[12px] overflow-hidden xl:w-[214px] xl:h-[142px] xl:shrink-0 xl:mb-0">
              <img src={rmVars.themeUrl + 'images/college-humanity.png'} className="w-full h-full object-cover" alt="college" />
            </div>

            <div>
              <p className="mb-2 text-[14px] text-[#555] font-semibold leading-[100%] xl-mb-[5px]">
                {record?.up_category_name || '—'}
              </p>
              <h2 className="mb-5 text-[22px] font-black leading-[130%] xl:mb-[14px]">
                {record?.category_name_en || '—'}
              </h2>
              <p className="text:[14px] text-[#8D9AAF] leading-[140%]">
                {record?.category_name || '\u00A0'}
              </p>
            </div>
          </div>

          {(record?.tel || record?.address || record?.website) && (
            <div className="flex flex-col gap-y-[14px] mt-6 py-6 border-y border-y-[#E4E8F1] text-[14px] font-semibold leading-[100%] xl:flex-row xl:items-center xl:flex-wrap xl:gap-x-3 xl:py-5 xl:mt-7 xl:gap-y-1">
              {record?.tel && (
                <>
                  <div className="flex gap-x-[10px] xl:items-center xl:gap-x-1">
                    <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                      <img src={rmVars.themeUrl + 'images/phone-ico.png'} className="hidden xl:block w-[18px]" alt="tel" />
                      <span className="xl:hidden">Tel</span>
                    </div>
                    <div className="xl:text-[#555]">
                      {record.tel && /^[\d-]{7,20}$/.test(record.tel.trim()) ? (
                        <a href={`tel:${record.tel.trim()}`} className="hover:underline">
                          {record.tel}
                        </a>
                      ) : (
                        record.tel
                      )}
                    </div>
                  </div>
                  {(record?.address || record?.website) && (
                    <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                  )}
                </>
              )}

              {record?.address && (
                <>
                  <div className="flex gap-x-[10px] xl:items-center xl:gap-x-1">
                    <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                      <img src={rmVars.themeUrl + 'images/spot-ico.png'} className="hidden xl:block w-[18px]" alt="address" />
                      <span className="xl:hidden">Address</span>
                    </div>
                    <div className="flex flex-col items-start gap-2 xl:text-[#555] xl:flex-row xl:items-center">
                      <span>{record.address}</span>
                      {record?.gps && (
                        <a href={record.gps} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-[5px] text-[#8D9AAF] font-medium text-[13px] border border-[#E4E8F1] rounded-[8px] xl:w-[26px] xl:h-[26px] xl:p-0 xl:justify-center">
                          <span className="xl:hidden">Map</span>
                          <img src={rmVars.themeUrl + 'images/google-ico.png'} className="w-[15px]" alt="map" />
                        </a>
                      )}
                    </div>
                  </div>
                  {record?.homepage && (
                    <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                  )}
                </>
              )}

              {record?.homepage && (
                <div className="flex gap-x-[10px] items-center">
                  <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto xl:flex xl:items-center xl:gap-1">
                    <img src={rmVars.themeUrl + 'images/website-ico.png'} className="hidden xl:block w-[18px]" alt="website" />
                    <span className="xl:text-[#555] xl:font-semibold">WebSite</span>
                  </div>
                  <a href={record.homepage} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-[5px] text-[#8D9AAF] font-medium text-[13px] border border-[#E4E8F1] rounded-[8px] xl:w-[26px] xl:h-[26px] xl:p-0 xl:justify-center">
                    <span className="xl:hidden">WebSite</span>
                    <img src={rmVars.themeUrl + 'images/launch-ico-gr.png'} className="w-[18px]" alt="website" />
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="mt-[18px] bg-white rounded-xl border border-[#E4E8F1] lg:mt-[50px] lg:border-none">

            <div className="journal-graph-toggle relative pt-[24px] pb-6 px-[20px] cursor-pointer lg:px-[25px] lg:pb-5 lg:pt-[20px] lg:hidden">
              <div className="flex items-center gap-3 pr-[30px] text-[15px] font-bold leading-[130%] lg:text-[18px] lg:gap-[6px]">
                <div className="flex shrink-0 justify-center items-center w-[38px] h-[38px] rounded-lg bg-[#E4E8F1] lg:w-auto lg:h-auto lg:bg-transparent">
                  <img src={rmVars.themeUrl + 'images/bar_chart_ico.png'} className="w-[22px]" alt="chart icon" />
                </div>
                Department Trends of Pusan National University
              </div>
              <img src={rmVars.themeUrl + 'images/expand_more_333.png'} className="absolute right-[20px] top-[50%] w-[20px] translate-y-[-50%] rotate-180 transition-transform duration-300 lg:w-[24px]" alt="toggle" />
            </div>

            <div className="border-t border-t-[#E4E8F1] pt-5 px-[20px] lg:p-0 lg:border-none">

              <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-20px)/2)] lg:bg-[#F3F5F9] lg:p-8 lg:mb-0 lg:hidden">
                <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:text-[16px]">
                  Keyword Cloud
                </h3>
                <div className="relative h-[250px]">
                  {keywordLoading ? (
                    <ChartSkeletonLoader show={keywordLoading} />
                  ) : Array.isArray(keywordData) && keywordData.length > 0 ? (
                    <KeywordCloud items={keywordData} maxWordsCollapsed={100} maxWords={0} />
                  ) : null}
                </div>
              </div>

              <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-20px)/2)] lg:bg-[#F3F5F9] lg:p-8 lg:mb-0 lg:hidden">
                <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center">
                  Publication Trends (Past 5 Years)
                </h3>
                <DepartmentOutput5YearChart departmentCode={record?.category_id} />
              </div>

              <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:p-0 lg:mb-[60px] lg:border-none lg:rounded-0">
                <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:mb-6 lg:text-[18px] lg:text-[#333] lg:text-left">
                  Top 5 most published categories by DB
                </h3>

                <TopCategoriesByDB deptId={record?.category_id} mode="detail" />
              </div>

              <div className="flex flex-col lg:flex-row lg:gap-x-[30px]">
                <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-30px)/2)] lg:p-0 lg:mb-[60px] lg:border-none lg:rounded-0">
                  <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:mb-6 lg:text-[18px] lg:text-[#333] lg:text-left">
                    Interdepartmental Collaboration
                  </h3>

                  <div className="lg:p-8 lg:border lg:border-[#E4E8F1] lg:rounded-[12px]">
                    <DepartmentCollaborationBar
                      departmentCode={record?.category_id}
                      type="interdepartmental"
                    />
                  </div>
                </div>

                <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-30px)/2)] lg:p-0 lg:mb-[60px] lg:border-none lg:rounded-0">
                  <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:mb-6 lg:text-[18px] lg:text-[#333] lg:text-left">
                    Institutional Collaboration
                  </h3>

                  <div className="lg:p-8 lg:border lg:border-[#E4E8F1] lg:rounded-[12px]">
                    <DepartmentCollaborationBar
                      departmentCode={record?.category_id}
                      type="institutional"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row lg:gap-x-[30px]">
                <DepartmentMostCitedResearcherCard
                  deptId={record?.category_id}
                  startYear={topStartYear}
                  endYear={topEndYear}
                />

                <DepartmentTopPublishingResearcherCard
                  deptId={record?.category_id}
                  startYear={topStartYear}
                  endYear={topEndYear}
                />
              </div>

              <div className="flex justify-center pb-5 lg:hidden">
                <button className="flex items-center gap-[2px] px-[10px] py-1 text-[13px] font-semibold text-[#8D9AAF]">
                  <img src={rmVars.themeUrl + 'images/expand_more_8D9AAF.png'} className="w-[14px] rotate-180 cursor-pointer" alt="hide" />
                  Hide Chart
                </button>
              </div>

            </div>
          </div>

          <DepartmentTabsContent
            allScholars={allScholars}
            scholarsListRef={scholarsListRef}
            deptId={record?.id || record?.category_id || code}
          />
        </div>
      </div>

      <div className="right-box hidden relative mt-[105px] w-[calc((100%-40px)*0.3)] shrink-0 lg:block">
        <div className="w-full p-10 bg-white rounded-xl">
          <div className="mb-10">
            <h2 className="mb-[14px]">
              <span className="text-[16px] font-bold leading-[100%]">Keyword Cloud</span>
            </h2>

            <div className="p-8 border border-[#E4E8F1] rounded-[12px]">
              <div className="relative h-[300px]">
                {keywordLoading ? (
                  <ChartSkeletonLoader show={keywordLoading} />
                ) : Array.isArray(keywordData) && keywordData.length > 0 ? (
                  <KeywordCloud items={keywordData} maxWords={0} />
                ) : null}
              </div>
            </div>
          </div>

          <div>
            <h2 className="mb-[14px]">
              <span className="text-[16px] font-bold leading-[100%]">
                Publication Trends (Past 5 Years)
              </span>
            </h2>

            <div className="p-8 border border-[#E4E8F1] rounded-[12px]">
              <DepartmentOutput5YearChart departmentCode={record?.id || record?.category_id || code} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
